/**
 * PageHeader — the single, consistent header for every internal page.
 * Icon badge + serif title + mono subtitle on the left; actions on the right.
 * Use it at the top of each view so pages feel cohesive when navigating.
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface PageHeaderProps {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /** Extra classes for the outer container (e.g. md:shrink-0 for fixed-height pages). */
  className?: string;
}

export function PageHeader({ icon, title, subtitle, actions, className }: PageHeaderProps) {
  return (
    <div
      className={cn(
        // Uma linha só: título à esquerda, ações (ex.: Voltar no mobile) à direita.
        'border-b border-border pb-4 mb-5 md:pb-5 md:mb-6',
        'flex flex-row items-center justify-between gap-3 md:gap-4',
        className,
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        {icon && (
          <div className="shrink-0 rounded-xl border border-primary/20 bg-primary/10 p-2 text-primary">
            {icon}
          </div>
        )}
        <div className="min-w-0">
          <h1 className="font-serif text-xl md:text-2xl font-bold text-foreground leading-tight truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-0.5 truncate font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex items-center gap-2 shrink-0 overflow-x-auto md:overflow-visible -mx-1 px-1 md:mx-0 md:px-0">
          {actions}
        </div>
      )}
    </div>
  );
}
